import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { FuelPlanFuelStop, FuelPlanStop } from "types";
import {
  formatMiles,
  getFuelStopLocation,
  SelectedFuelStops,
} from "pages/fuel-plan/fuel-plan-display";
import { lockFuelStopModalBackground } from "./fuel-stop-modal-scroll-lock";

export type FuelStopModalState = {
  isOpen: boolean;
  recommendationSequence: number;
  regionName: string;
  fuelStops: FuelPlanFuelStop[];
};

type FuelStopModalProps = {
  modal: FuelStopModalState;
  selectedFuelStops: SelectedFuelStops;
  onClose: () => void;
  onSelectFuelStop: (
    recommendationSequence: number,
    fuelStop: FuelPlanFuelStop
  ) => void;
  onViewFuelStopsOnMap: (
    recommendationSequence: number,
    fuelStops: FuelPlanFuelStop[]
  ) => void;
};

export const createClosedFuelStopModal = (): FuelStopModalState => ({
  isOpen: false,
  recommendationSequence: 0,
  regionName: "",
  fuelStops: [],
});

export const createFuelStopModal = (
  recommendation: FuelPlanStop
): FuelStopModalState => ({
  isOpen: true,
  recommendationSequence: recommendation.sequence,
  regionName: recommendation.displayName || recommendation.state || "",
  fuelStops: recommendation.fuelStops,
});

const FuelStopModal = ({
  modal,
  selectedFuelStops,
  onClose,
  onSelectFuelStop,
  onViewFuelStopsOnMap,
}: FuelStopModalProps) => {
  useEffect(() => {
    if (!modal.isOpen) return;

    const unlockBackground = lockFuelStopModalBackground();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      unlockBackground();
    };
  }, [modal.isOpen, onClose]);

  if (!modal.isOpen) return null;

  const selectedFuelStopId =
    selectedFuelStops[modal.recommendationSequence]?.id;
  const regionName = modal.regionName || "Recommended Area";

  return createPortal(
    <div className="fuel-stop-modal-backdrop" onClick={onClose}>
      <div
        aria-labelledby="fuel-stop-modal-title"
        aria-modal="true"
        className="fuel-stop-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="fuel-stop-modal-header">
          <div>
            <h2 id="fuel-stop-modal-title">Fuel Stops in {regionName}</h2>
            <p>
              Tap a stop to use it for this fuel recommendation and update the
              displayed mileage.
            </p>
          </div>
          <button
            aria-label="Close fuel stops"
            className="fuel-stop-modal-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>
        <div className="fuel-stop-modal-body">
          {modal.fuelStops.length > 0 ? (
            <div className="fuel-stop-choice-list">
              {modal.fuelStops.map((fuelStop, index) => {
                const isSelected = selectedFuelStopId === fuelStop.id;

                return (
                  <button
                    className={`fuel-stop-choice ${
                      isSelected ? "fuel-stop-choice-selected" : ""
                    }`}
                    key={fuelStop.id || `${fuelStop.name}-${index}`}
                    onClick={() =>
                      onSelectFuelStop(modal.recommendationSequence, fuelStop)
                    }
                    type="button"
                  >
                    <span
                      className={`fuel-stop-choice-index ${
                        isSelected ? "fuel-stop-choice-index-selected" : ""
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="fuel-stop-choice-main">
                      <strong>{fuelStop.name}</strong>
                      <small>
                        {getFuelStopLocation(fuelStop) ||
                          fuelStop.state ||
                          "Preferred fuel stop"}
                      </small>
                    </span>
                    <span className="fuel-stop-choice-metrics">
                      <span className="fuel-stop-choice-metric">
                        <small>To Stop</small>
                        <strong>
                          {formatMiles(fuelStop.milesFromPreviousFuelStop)}
                        </strong>
                      </span>
                      <span className="fuel-stop-choice-metric">
                        <small>Next Leg</small>
                        <strong>{formatMiles(fuelStop.milesToNextStop)}</strong>
                      </span>
                      {fuelStop.distanceOffRouteMiles !== null ? (
                        <span className="fuel-stop-choice-metric">
                          <small>Off Route</small>
                          <strong>
                            {formatMiles(fuelStop.distanceOffRouteMiles)}
                          </strong>
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="fuel-stop-modal-empty">
              <strong>
                No in-network fuel stops are available within range.
              </strong>
              <p>
                Use the nearest safe fuel station or the lowest-cost option
                available on your route in {regionName}.
              </p>
            </div>
          )}
        </div>
        {modal.fuelStops.length > 0 ? (
          <div className="fuel-stop-modal-footer">
            <button
              className="fuel-stop-modal-map-button"
              onClick={() =>
                onViewFuelStopsOnMap(
                  modal.recommendationSequence,
                  modal.fuelStops
                )
              }
              type="button"
            >
              View All on Designated Route
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default FuelStopModal;
